import { isMapStyleReady } from '../mapcore.js';
// Layer "campi & analisi" a punti v2 — port 1:1 dei layer v1:
//  - tempVariation: map-temperature-variation.js (variazione temperatura 1/12/24h)
//  - rainAccum:     map-rain-accumulation.js (pioggia cumulata 24/48/72/96/120h)
// In v1 ogni stazione è un divIcon SVG (cerchio colorato + valore); qui il
// DISEGNO è replicato su canvas come sprite (pattern stations.js/station-sprites.js:
// map.addImage con pixelRatio ≤2.5, ancora al centro di un canvas simmetrico,
// symbol layer, guard isStyleLoaded con retry su once('idle'), prune atlas).
// Hover = tooltip breve (bindTooltip v1), click = popup completo (bindPopup v1).
// Cache dati con TTL come v1 (temp 60s, rain 5min) + retry con timeout.
// NB: la mutua esclusione "uno alla volta" NON è gestita qui (la fa il pannello).
// Globali richiesti: chroma (vendor), window.WSPalettes (map-palettes.js v1),
// window.WSWeatherApi.
import { stationNetworkFullLabel } from '../lib/params.js';
import { setStationsFieldSuppressed } from './stations.js';
import { ensureTemperatureField, renderTemperatureField, clearTemperatureField } from './analysis-temperature-field.js';

const MAX_ATLAS_IMAGES = 400; // oltre: prune delle immagini non più usate

export const ANALYSIS_KEYS = ['tempVariation', 'rainAccum'];

// ---------------------------------------------------------------------------
// helper (port 1:1 dai moduli v1)
// ---------------------------------------------------------------------------

function finiteValue(value) {
    if (value === null || value === undefined || value === '') return null;
    const n = Number.parseFloat(value);
    return Number.isFinite(n) ? n : null;
}

function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>"']/g, ch => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
    }[ch]));
}

// I timestamp dell'API sono UTC ("2026-06-25 00:20:00" senza suffisso): vanno
// resi in Europe/Rome (coerente con v1, append 'Z').
function formatTimeHm(value) {
    if (!value) return '';
    const iso = String(value).replace(' ', 'T');
    const date = new Date(/[zZ]|[+\-]\d\d:?\d\d$/.test(iso) ? iso : iso + 'Z');
    if (isNaN(date)) return '';
    return new Intl.DateTimeFormat('it-IT', {
        hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome'
    }).format(date);
}

function formatTimeDmHm(value) {
    if (!value) return '';
    const iso = String(value).replace(' ', 'T');
    const date = new Date(/[zZ]|[+\-]\d\d:?\d\d$/.test(iso) ? iso : iso + 'Z');
    if (isNaN(date)) return '';
    return new Intl.DateTimeFormat('it-IT', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome'
    }).format(date);
}

function networkLabel(row) {
    try { return stationNetworkFullLabel(row) || 'Rete WS'; }
    catch (e) { return String(row?.network || 'Rete WS'); }
}

// --- tempVariation: colore delta (v1 variationColor) ------------------------
function variationScaleMax(hours) {
    return hours === 1 ? 3 : hours === 12 ? 6 : 8;
}

function variationColor(value) {
    const max = variationScaleMax(state.hours.tempVariation);
    const n = Math.max(-max, Math.min(max, Number(value) || 0));
    if (Math.abs(n) < 0.15) return '#f8fafc';
    return chroma.scale(['#2b83ba', '#f8fafc', '#d7191c']).domain([-max, 0, max]).mode('lab')(n).hex();
}

// v1 textPalette (variazione temperatura: soglia 0.46, testo con alone)
function tempTextPalette(color) {
    const c = chroma(color);
    if (c.luminance() < 0.46) return { fill: '#fff', stroke: 'rgba(15,23,42,0.45)' };
    return { fill: '#102033', stroke: 'rgba(255,255,255,0.94)' };
}

function formatDelta(value) {
    const n = finiteValue(value);
    if (n === null) return '';
    const text = Math.abs(n) < 10 ? n.toFixed(1) : n.toFixed(0);
    return n > 0 ? `+${text}` : text;
}

// --- rainAccum: palette "pioggia" condivisa (v1 rainColor) -------------------
function rainColor(mm) {
    const v = Math.max(0, Number(mm) || 0);
    try { return window.WSPalettes.scale('pioggia')(v).hex(); }
    catch (e) { return '#1c71f7'; }
}

// v1 textPalette (pioggia cumulata: soglia 0.5, testo senza alone)
function rainTextPalette(color) {
    const c = chroma(color);
    if (c.luminance() < 0.5) return { fill: '#fff', stroke: 'rgba(15,23,42,0.45)' };
    return { fill: '#102033', stroke: 'rgba(255,255,255,0.94)' };
}

function formatMm(value) {
    const n = finiteValue(value);
    if (n === null) return '';
    return n < 10 ? n.toFixed(1) : String(Math.round(n));
}

function gradientBar(colorFn, min, max, steps = 32) {
    const out = [];
    for (let i = 0; i <= steps; i++) {
        const pos = i / steps;
        const value = min + (max - min) * pos;
        out.push({ pos, color: colorFn(value) });
    }
    return out;
}

function analysisLegendSpec(key) {
    const hours = state.hours[key];
    if (key === 'tempVariation') {
        const max = variationScaleMax(hours);
        return {
            title: `Variazione temperatura ${hours}h`,
            unit: '°C · colore stimato',
            bar: gradientBar(variationColor, -max, max),
            ticks: [
                { pos: 0, label: `-${max}` },
                { pos: 0.25, label: `-${max / 2}` },
                { pos: 0.5, label: '0' },
                { pos: 0.75, label: `+${max / 2}` },
                { pos: 1, label: `+${max}` },
            ],
        };
    }
    const max = 300;
    return {
        title: `Pioggia cumulata ${hours / 24}g`,
        unit: 'mm',
        bar: gradientBar(rainColor, 0, max, 48),
        ticks: [
            { pos: 0, label: '0' },
            { pos: 10 / max, label: '10' },
            { pos: 50 / max, label: '50' },
            { pos: 100 / max, label: '100' },
            { pos: 200 / max, label: '200' },
            { pos: 1, label: '300' },
        ],
    };
}

function publishAnalysisLegend(key, on) {
    document.dispatchEvent(new CustomEvent('wsv2:map-legend', {
        detail: { id: `analysis-${key}`, spec: on ? analysisLegendSpec(key) : null },
    }));
}

// Raggio zoom-dipendente identico alla v1 (markerIcon):
// zoom ≤8 → 17, ≤10 → 19, altrimenti 22 (il marker scala con lo zoom).
function markerRadius(zoom) {
    return zoom <= 8 ? 17 : (zoom <= 10 ? 19 : 22);
}

// ---------------------------------------------------------------------------
// sprite canvas — replica 1:1 del divIcon SVG v1
// tempVariation (map-temperature-variation.js ~r.108): cerchio pieno colorato
//   stroke bianco 1.5, anello interno r-3 rgba(16,32,51,0.12) 1px, testo 850
//   con alone (paint-order:stroke, stroke-width 1.8).
// rainAccum (map-rain-accumulation.js ~r.94): cerchio pieno colorato stroke
//   bianco 1.3, testo 760 senza alone.
// Canvas (r+4)*2 quadrato, ancora esattamente al centro (come iconAnchor v1)
// → già simmetrico, icon-anchor:'center' posiziona il centro sulla coordinata.
// ---------------------------------------------------------------------------

function spriteDpr() {
    return Math.max(1, Math.min(window.devicePixelRatio || 1, 2.5));
}

function buildAnalysisSprite(kind, text, color, r) {
    const dpr = spriteDpr();
    const w = (r + 4) * 2;
    const fontSize = r <= 17 ? 10.5 : (r <= 19 ? 11.5 : 13);
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(w * dpr);
    canvas.height = Math.ceil(w * dpr);
    const ctx = canvas.getContext('2d', { alpha: true });
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineJoin = 'round';
    const cx = w / 2;
    const cy = w / 2;

    if (kind === 'tempVariation') {
        const palette = tempTextPalette(color);
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = 'rgba(255,255,255,0.96)';
        ctx.stroke();
        // anello interno sottile (v1: <circle r-3 stroke rgba(16,32,51,0.12)>)
        ctx.beginPath();
        ctx.arc(cx, cy, r - 3, 0, Math.PI * 2);
        ctx.lineWidth = 1;
        ctx.strokeStyle = 'rgba(16,32,51,0.12)';
        ctx.stroke();
        // testo 850 con alone: paint-order:stroke → prima strokeText poi fillText
        ctx.font = `850 ${fontSize}px Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'alphabetic';
        ctx.lineWidth = 1.8;
        ctx.strokeStyle = palette.stroke;
        ctx.strokeText(text, cx, cy + 4);
        ctx.fillStyle = palette.fill;
        ctx.fillText(text, cx, cy + 4);
    } else {
        const palette = rainTextPalette(color);
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.lineWidth = 1.3;
        ctx.strokeStyle = 'rgba(255,255,255,0.96)';
        ctx.stroke();
        // testo 760 senza alone (la v1 non ha stroke sul testo pioggia)
        ctx.font = `760 ${fontSize}px Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'alphabetic';
        ctx.fillStyle = palette.fill;
        ctx.fillText(text, cx, cy + 4);
    }

    return { data: ctx.getImageData(0, 0, canvas.width, canvas.height), pixelRatio: dpr };
}

function spriteKey(kind, text, color, r) {
    // palette testo deterministica dal colore → non serve nella chiave
    return `anl|${kind}|${r}|${color}|${text}`;
}

// ---------------------------------------------------------------------------
// stato modulo
// ---------------------------------------------------------------------------

const CFG = {
    tempVariation: {
        src: 'ws-anl-tempvar',
        allowedHours: [1, 12, 24],
        defaultHours: 1,
        cacheTtlMs: 60 * 1000,           // v1 CACHE_TTL_MS temp-variation
        timeouts: [5000, 9000],          // v1 fetchOnce: primo tentativo / retry
        fetch: hours => window.WSWeatherApi.getTemperatureVariations({ hours }),
        timeoutLabel: 'temp-variation fetch timeout',
        value: row => finiteValue(row.delta ?? row.value),
    },
    rainAccum: {
        src: 'ws-anl-rainaccum',
        allowedHours: [24, 48, 72, 96, 120],
        defaultHours: 24,
        cacheTtlMs: 5 * 60 * 1000,       // v1 CACHE_TTL_MS rain-accumulation
        timeouts: [6000, 10000],
        fetch: hours => window.WSWeatherApi.getRainAccumulation({ hours }),
        timeoutLabel: 'rain-accum fetch timeout',
        value: row => finiteValue(row.value ?? row.rain_mm),
    },
};

const state = {
    map: null,
    enabled: { tempVariation: false, rainAccum: false },
    tempVariationMarkersVisible: true,
    hours: { tempVariation: 1, rainAccum: 24 },
    // Token di serializzazione dei render (pattern renderSeq v1): solo l'ultimo
    // render committa sulla source; i precedenti abortiscono PRIMA di toccarla.
    seq: { tempVariation: 0, rainAccum: 0 },
    cache: { tempVariation: new Map(), rainAccum: new Map() },   // hours → {at, payload}
    meta: { tempVariation: null, rainAccum: null },
    wired: { tempVariation: false, rainAccum: false },
    pendingIdle: { tempVariation: false, rainAccum: false },
    usedKeys: { tempVariation: new Set(), rainAccum: new Set() },  // sprite in uso per layer
    _images: new Set(),
    _debounce: null,
    _tempRefreshTimer: null,
};

// ---------------------------------------------------------------------------
// layer MapLibre (guard isStyleLoaded con retry su once('idle'), pattern stations.js)
// ---------------------------------------------------------------------------

function ensureLayer(key) {
    const map = state.map;
    const id = CFG[key].src;
    if (map.getSource(id)) {
        if (key === 'tempVariation') ensureTemperatureField(map, id);
        return true;
    }
    if (!isMapStyleReady(map)) {
        if (!state.pendingIdle[key]) {
            state.pendingIdle[key] = true;
            map.once('idle', () => {
                state.pendingIdle[key] = false;
                if (state.enabled[key]) render(key).catch(() => {});
            });
        }
        return false;
    }
    map.addSource(id, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addLayer({
        id,
        type: 'symbol',
        source: id,
        layout: {
            ...(key === 'tempVariation' ? { visibility: state.tempVariationMarkersVisible ? 'visible' : 'none' } : {}),
            'icon-image': ['get', 'sk'],
            'icon-allow-overlap': key !== 'tempVariation',
            'icon-ignore-placement': key !== 'tempVariation',
            // Impilamento come i divIcon DOM v1: chi sta più in basso sullo
            // schermo copre chi sta sopra.
            'symbol-z-order': 'viewport-y',
            'symbol-sort-key': ['get', 'y'],
        },
    });
    if (key === 'tempVariation') ensureTemperatureField(map, id);
    wireInteractions(key);
    return true;
}

function setSourceData(key, features) {
    const src = state.map && state.map.getSource(CFG[key].src);
    if (src) src.setData({ type: 'FeatureCollection', features });
}

// ---------------------------------------------------------------------------
// interazioni: hover = tooltip breve (bindTooltip v1), click = popup completo
// (bindPopup v1, className/maxWidth identici)
// ---------------------------------------------------------------------------

let hoverPopup = null;
let clickPopup = null;

function ensureHoverPopup() {
    if (!hoverPopup) {
        hoverPopup = new maplibregl.Popup({
            closeButton: false, closeOnClick: false, offset: 10,
            className: 'wsv2-tooltip-shell ws-station-tooltip-shell', maxWidth: '260px',
        });
    }
    return hoverPopup;
}

function wireInteractions(key) {
    if (state.wired[key]) return;
    state.wired[key] = true;
    const map = state.map;
    const lyr = CFG[key].src;
    map.on('mousemove', lyr, (e) => {
        const f = e.features && e.features[0];
        if (!f || !f.properties.tt) return;
        map.getCanvas().style.cursor = 'pointer';
        ensureHoverPopup()
            .setLngLat(f.geometry.coordinates)
            .setHTML(`<div class="ws-derived-tooltip">${f.properties.tt}</div>`)
            .addTo(map);
    });
    map.on('mouseleave', lyr, () => {
        map.getCanvas().style.cursor = '';
        if (hoverPopup) hoverPopup.remove();
    });
    map.on('click', lyr, (e) => {
        const f = e.features && e.features[0];
        if (!f || !f.properties.pp) return;
        if (hoverPopup) hoverPopup.remove();
        if (clickPopup) clickPopup.remove();
        clickPopup = new maplibregl.Popup({
            closeButton: true, closeOnClick: true, offset: 12,
            className: 'wsv2-tooltip-shell ws-temp-variation-popup-shell', maxWidth: '290px',
        }).setLngLat(f.geometry.coordinates).setHTML(f.properties.pp).addTo(map);
    });
}

// ---------------------------------------------------------------------------
// popup completi (port 1:1 popupHtml v1)
// ---------------------------------------------------------------------------

function tempVariationPopup(row, hours) {
    const name = row.name || row.nome || 'Stazione';
    const current = finiteValue(row.current_temperature);
    const reference = finiteValue(row.reference_temperature);
    const delta = finiteValue(row.delta ?? row.value);
    return `
        <article class="ws-temp-variation-popup">
            <span>${escapeHtml(networkLabel(row))}</span>
            <h3>${escapeHtml(name)}</h3>
            <strong>${escapeHtml(formatDelta(delta))}<small>°C</small></strong>
            <dl>
                <div><dt>Adesso</dt><dd>${current === null ? 'n.d.' : escapeHtml(current.toFixed(1))} °C</dd></div>
                <div><dt>${hours}h fa</dt><dd>${reference === null ? 'n.d.' : escapeHtml(reference.toFixed(1))} °C</dd></div>
                <div><dt>Ora live</dt><dd>${escapeHtml(formatTimeHm(row.observed_at) || 'n.d.')}</dd></div>
                <div><dt>Rif.</dt><dd>${escapeHtml(formatTimeHm(row.reference_observed_at) || 'n.d.')}</dd></div>
            </dl>
        </article>
    `;
}

function rainAccumPopup(row, hours) {
    const name = row.name || row.nome || 'Stazione';
    const value = finiteValue(row.value ?? row.rain_mm);
    const days = hours / 24;
    return `
        <article class="ws-temp-variation-popup">
            <span>${escapeHtml(networkLabel(row))}</span>
            <h3>${escapeHtml(name)}</h3>
            <strong>${value === null ? 'n.d.' : escapeHtml(formatMm(value))}<small>mm</small></strong>
            <dl>
                <div><dt>Finestra</dt><dd>ultimi ${days} ${days === 1 ? 'giorno' : 'giorni'}</dd></div>
                <div><dt>Ultimo dato</dt><dd>${escapeHtml(formatTimeDmHm(row.observed_at) || 'n.d.')}</dd></div>
            </dl>
        </article>
    `;
}

// ---------------------------------------------------------------------------
// fetch con cache TTL + retry con timeout (port 1:1 fetchOnce/fetchRows v1):
// alla PRIMA attivazione la richiesta può restare appesa a lungo → race con
// timeout corto e retry su connessione ormai calda.
// ---------------------------------------------------------------------------

function fetchOnce(key, hours, timeoutMs) {
    return Promise.race([
        CFG[key].fetch(hours),
        new Promise((_, reject) => setTimeout(() => reject(new Error(CFG[key].timeoutLabel)), timeoutMs)),
    ]);
}

async function fetchRows(key, hours) {
    const cache = state.cache[key];
    const cacheKey = String(hours);
    const cached = cache.get(cacheKey);
    if (cached && Date.now() - cached.at < CFG[key].cacheTtlMs) return cached.payload;
    let payload;
    try {
        payload = await fetchOnce(key, hours, CFG[key].timeouts[0]);
    } catch (err) {
        await new Promise(resolve => setTimeout(resolve, 400));
        payload = await fetchOnce(key, hours, CFG[key].timeouts[1]);
    }
    cache.set(cacheKey, { at: Date.now(), payload });
    return payload;
}

// ---------------------------------------------------------------------------
// render: righe → sprite canvas → feature symbol (pattern render() stations.js)
// ---------------------------------------------------------------------------

async function render(key) {
    const map = state.map;
    if (!map || !state.enabled[key]) return;
    const seq = ++state.seq[key];
    const hours = state.hours[key];
    const response = await fetchRows(key, hours);
    // Un render più recente è partito mentre aspettavamo la rete (o il layer è
    // stato spento): abortiamo PRIMA di toccare la source.
    if (seq !== state.seq[key] || !state.enabled[key]) return;
    if (!ensureLayer(key)) return;
    state.meta[key] = response.meta || null;
    const valueOf = CFG[key].value;
    const rows = (Array.isArray(response.points) ? response.points : [])
        .filter(row => finiteValue(row.lat) !== null && finiteValue(row.lng) !== null && valueOf(row) !== null);

    const r = key === 'tempVariation' ? 17 : markerRadius(map.getZoom());
    const features = [];
    const used = new Set();
    for (const row of rows) {
        const value = valueOf(row);
        const isTemp = key === 'tempVariation';
        const color = isTemp ? variationColor(value) : rainColor(value);
        const text = isTemp ? formatDelta(value) : formatMm(value);
        const name = row.name || row.nome || 'Stazione';
        const sk = spriteKey(key, text, color, r);
        used.add(sk);
        if (!map.hasImage(sk)) {
            const img = buildAnalysisSprite(key, text, color, r);
            map.addImage(sk, img.data, { pixelRatio: img.pixelRatio });
            state._images.add(sk);
        }
        const tt = isTemp
            ? `${escapeHtml(name)}: ${escapeHtml(text)}°C`
            : `${escapeHtml(name)}: ${escapeHtml(text)} mm`;
        const pp = isTemp ? tempVariationPopup(row, hours) : rainAccumPopup(row, hours);
        features.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [Number(row.lng), Number(row.lat)] },
            properties: { sk, tt, pp, y: Number(row.lat) * -1 },
        });
    }
    setSourceData(key, features);
    state.usedKeys[key] = used;
    if (key === 'tempVariation') {
        try {
            await renderTemperatureField(map,
                rows.map(row => ({ ...row, delta: valueOf(row) })), variationColor,
                () => state.enabled[key] && seq === state.seq[key]);
        } catch (error) {
            clearTemperatureField(map);
            console.warn('Campo variazione temperatura non disponibile:', error);
        }
        if (seq !== state.seq[key] || !state.enabled[key]) return;
    }

    // Prune atlas: se il registro cresce troppo, via le immagini non più usate
    // da NESSUNO dei due layer.
    if (state._images.size > MAX_ATLAS_IMAGES) {
        const stillUsed = new Set();
        ANALYSIS_KEYS.forEach(k => {
            if (state.enabled[k]) state.usedKeys[k].forEach(sk => stillUsed.add(sk));
        });
        for (const sk of Array.from(state._images)) {
            if (!stillUsed.has(sk)) {
                try { map.removeImage(sk); } catch (e) {}
                state._images.delete(sk);
            }
        }
    }
}

// I marker della variazione termica hanno dimensione fissa e MapLibre gestisce
// le collisioni: al cambio zoom non serve ricalcolare né il campo né gli sprite.
// La pioggia cumulata mantiene il ridimensionamento originale dei marker.
function debouncedRender() {
    if (state._debounce) clearTimeout(state._debounce);
    state._debounce = setTimeout(() => {
        state._debounce = null;
        if (state.enabled.rainAccum) render('rainAccum').catch(() => {});
    }, 200);
}

// ---------------------------------------------------------------------------
// API pubblica
// ---------------------------------------------------------------------------

export function initAnalysisPoints(map) {
    state.map = map;
    map.on('zoomend', debouncedRender);
    if (state._tempRefreshTimer) clearInterval(state._tempRefreshTimer);
    state._tempRefreshTimer = setInterval(() => {
        if (state.enabled.tempVariation && document.visibilityState === 'visible') {
            render('tempVariation').catch(() => {});
        }
    }, 90 * 1000);
}

export function setAnalysisEnabled(key, on) {
    if (!CFG[key] || !state.map) return;
    on = !!on;
    if (on === state.enabled[key]) return;
    state.enabled[key] = on;
    if (on) {
        publishAnalysisLegend(key, true);
        render(key).catch(err => console.warn(`[wsv2] analisi '${key}': fetch fallita`, err));
    } else {
        state.seq[key]++;  // invalida render in volo
        setSourceData(key, []);
        if (key === 'tempVariation') clearTemperatureField(state.map);
        publishAnalysisLegend(key, false);
        if (hoverPopup) hoverPopup.remove();
        if (clickPopup) { clickPopup.remove(); clickPopup = null; }
    }
    // Questi campi disegnano già un cerchio per-stazione: finché almeno uno è
    // attivo sopprimo i marker parametro delle stazioni (si sovrapporrebbero e
    // confonderebbero). Reversibile: spenti tutti, i marker tornano com'erano.
    setStationsFieldSuppressed(ANALYSIS_KEYS.some(k => state.enabled[k]));
}

export function setTempVariationHours(h) {
    const hours = Number(h);
    state.hours.tempVariation = CFG.tempVariation.allowedHours.includes(hours)
        ? hours : CFG.tempVariation.defaultHours;
    if (state.enabled.tempVariation) publishAnalysisLegend('tempVariation', true);
    if (state.enabled.tempVariation) render('tempVariation').catch(() => {});
}

export function setTempVariationMarkersVisible(on) {
    state.tempVariationMarkersVisible = !!on;
    const map = state.map;
    if (map?.getLayer(CFG.tempVariation.src)) {
        map.setLayoutProperty(CFG.tempVariation.src, 'visibility', on ? 'visible' : 'none');
    }
    if (!on) {
        if (hoverPopup) hoverPopup.remove();
        if (clickPopup) { clickPopup.remove(); clickPopup = null; }
    }
}

export function setRainAccumHours(h) {
    const hours = Number(h);
    state.hours.rainAccum = CFG.rainAccum.allowedHours.includes(hours)
        ? hours : CFG.rainAccum.defaultHours;
    if (state.enabled.rainAccum) publishAnalysisLegend('rainAccum', true);
    if (state.enabled.rainAccum) render('rainAccum').catch(() => {});
}
